// Split maths for the Add expense sheet. Everything is in whole cents so the
// shares always add up to the total exactly — no phantom balances after
// everyone pays (the 5¢ display rule in balance-display.ts stays as a net).

export type SplitMode = "equal" | "exact" | "percent" | "shares";

export const toCents = (v: string | number): number => {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

/** Split `total` cents by `weights`, handing leftover cents to the largest
 *  remainders first, so the result always sums to `total`. */
export function allocCents(total: number, weights: number[]): number[] {
  const sum = weights.reduce((a, b) => a + b, 0);
  if (!sum || total <= 0) return weights.map(() => 0);
  const raw = weights.map((w) => (total * w) / sum);
  const out = raw.map(Math.floor);
  let left = total - out.reduce((a, b) => a + b, 0);
  raw
    .map((r, i) => [r - out[i], i] as const)
    .sort((a, b) => b[0] - a[0])
    .forEach(([, i]) => { if (left > 0) { out[i]++; left--; } });
  return out;
}

export interface SplitInput {
  mode: SplitMode;
  ids: string[];                     // people in the split, in display order
  baseCents: number;                 // the typed amount (before tax & tip)
  totalCents: number;                // base + tax + tip
  hasAdjustments: boolean;           // tax or tip on
  exact: Record<string, number>;     // cents the user typed, keyed by id
  percent: Record<string, number>;   // % the user typed, keyed by id
  shares: Record<string, number>;    // share counts (default 1)
}

export interface SplitRow {
  id: string;
  cents: number;                     // final share, tax & tip included
  value?: string;                    // what the input shows (exact / %)
  set?: boolean;                     // the user typed this one
  count?: number;                    // shares
}

export interface SplitResult {
  rows: SplitRow[];
  ok: boolean;                       // shares add up to the total
  message: string;                   // "✓ Adds up…" or what's wrong
}

/** Exact: typed rows are kept, everyone else splits what's left. With tax or
 *  tip on, the typed amounts are what each person ordered and tax & tip are
 *  shared in proportion. Percent works the same way over 100%. */
export function computeSplit(s: SplitInput): SplitResult {
  const { ids, totalCents } = s;
  const money = (c: number) => `$${(c / 100).toFixed(2)}`;

  if (s.mode === "equal") {
    const cents = allocCents(totalCents, ids.map(() => 1));
    return { rows: ids.map((id, i) => ({ id, cents: cents[i] })), ok: ids.length > 0, message: "" };
  }

  if (s.mode === "exact") {
    const target = s.hasAdjustments ? s.baseCents : totalCents;
    const locked = ids.reduce((a, id) => a + (id in s.exact ? s.exact[id] : 0), 0);
    const free = ids.filter((id) => !(id in s.exact));
    const spread = allocCents(Math.max(0, target - locked), free.map(() => 1));
    const pre = ids.map((id) => (id in s.exact ? s.exact[id] : spread[free.indexOf(id)]));
    const preSum = pre.reduce((a, b) => a + b, 0);
    const final = s.hasAdjustments ? allocCents(totalCents, pre) : pre;
    const diff = target - preSum;
    return {
      rows: ids.map((id, i) => ({ id, cents: final[i], value: (pre[i] / 100).toFixed(2), set: id in s.exact })),
      ok: diff === 0 && preSum > 0,
      message: diff === 0
        ? `✓ Adds up to ${money(target)}${s.hasAdjustments ? " before tax & tip" : ""}`
        : diff > 0 ? `${money(diff)} left to assign` : `${money(-diff)} over the total`,
    };
  }

  if (s.mode === "percent") {
    const locked = ids.reduce((a, id) => a + (id in s.percent ? s.percent[id] : 0), 0);
    const free = ids.filter((id) => !(id in s.percent));
    const each = free.length ? Math.max(0, 100 - locked) / free.length : 0;
    const pct = ids.map((id) => (id in s.percent ? s.percent[id] : each));
    const sum = pct.reduce((a, b) => a + b, 0);
    const ok = Math.abs(sum - 100) < 0.005;
    const final = allocCents(totalCents, pct);
    return {
      rows: ids.map((id, i) => ({ id, cents: final[i], value: String(+pct[i].toFixed(2)), set: id in s.percent })),
      ok,
      message: ok ? `✓ 100% · ${money(totalCents)}`
        : sum < 100 ? `${+(100 - sum).toFixed(2)}% left to assign` : `${+(sum - 100).toFixed(2)}% over`,
    };
  }

  const counts = ids.map((id) => s.shares[id] ?? 1);
  const n = counts.reduce((a, b) => a + b, 0);
  const final = allocCents(totalCents, counts);
  return {
    rows: ids.map((id, i) => ({ id, cents: final[i], count: counts[i] })),
    ok: n > 0,
    message: n > 0 ? `✓ ${n} share${n === 1 ? "" : "s"} · ${money(Math.round(totalCents / n))} per share` : "Give at least one person a share",
  };
}
